/**
 * A tile editor session: the terrain cell it was opened on, the custom tile being drawn, and the
 * temporary link that previews it in the level until it is saved or cancelled.
 */
import type { LevelDoc, PaletteEntry } from '../../model/format.ts';
import { sortKey } from '../../data/gameData.ts';
import {
  createCustomTile,
  customEntry,
  customSheet,
  customTileOf,
  findCustomTile,
  pushHistory,
  removeCustomTile,
  restoreTile,
  saveCustomSheet,
  setTilePixels,
  spritePixels,
  type CustomTileVersion,
} from '../../data/customTiles.ts';
import { compiledStyle, saveStyleOverride, setStyleOverride, styleData, styleOverride, terrainCellInfo, type StyleOverride, type TerrainCellInfo, type TerrainStyleData } from '../../terrain/index.ts';
import { variantSprites } from '../../terrain/variants.ts';
import { pairs, spriteTable, variantPe } from '../../terrain/ttShared.ts';
import { FILL, NONE } from '../../terrain/types.ts';
import { terrainMissing } from '../../model/bake.ts';
import { getContainer, type TileEditRequest } from '../store.ts';
import { layerTiles } from '../scene.ts';
import { getCell } from '../tileOps.ts';
import { applyFlags, applySwap, blank, getPx, learnSwap, paletteOf, parseHex, setPx, type Img } from './img.ts';
import { cardinalChoice, cornerTile, dirsOfCorner, linkSpikeCorner, solidOfDest } from '../spikes.ts';

export type LinkMode = 'replace' | 'add';

export interface EditSession {
  req: TileEditRequest;
  styleId: string;
  d: TerrainStyleData;
  info: TerrainCellInfo;
  /** case key the tile is linked to */
  key: string;
  /** colour variant names by index (index 0 is the canonical colours); empty without variants */
  variants: string[];
  /** colour key of the cell ('' = canonical) */
  cellColor: string;
  /** style choice the cell shows now */
  cur: number;
  curPe: PaletteEntry | null;
  /** tile flags the edited tile is shown with in the cell (the canvas shows it that way) */
  flags: number;
  tileN: number;
  created: boolean;
  /** style override before the session (restored on cancel) */
  prev: StyleOverride | null;
  /** tile as it was when the session started */
  snapshot: CustomTileVersion;
  /** choices the case had before the session (null: learned pieces) */
  baseList: number[] | null;
  /** id of the tile layer the case's tiles go to */
  editLayer: string | null;
}

export const colorKeyOf = (s: EditSession, vi: number) => (vi > 0 ? s.variants[vi] ?? '' : '');

function editLayerOf(doc: LevelDoc, req: TileEditRequest, styleId: string): string | null {
  const cont = getContainer(doc, req.container);
  const ofStyle = cont.terrains.filter((t) => t.mode === 'add' && t.style.split(':')[0] === styleId);
  const inside = ofStyle.find((t) => {
    const r = t.rings[0] ?? [];
    const xs = r.map((p) => p[0]), ys = r.map((p) => p[1]);
    return req.x >= Math.min(...xs) && req.x < Math.max(...xs) && req.y >= Math.min(...ys) && req.y < Math.max(...ys);
  });
  const t = inside ?? ofStyle[0];
  if (req.layer === 'spike') return cont.layers.find((l) => l.role === 'spikes')?.id ?? null;
  if (!t) return null;
  return (req.layer === 'dirt' ? t.targets.level : t.targets.foreground) ?? null;
}

function compositeSpikeSeed(styleId: string, corner: NonNullable<TileEditRequest['corner']>): Img {
  const out = blank(16);
  for (const dir of dirsOfCorner(corner)) {
    const ch = cardinalChoice(styleId, dir);
    if (!ch) continue;
    const px = spritePixels(ch.pe);
    if (!px) continue;
    const img = applyFlags(px, ch.flags);
    for (let y = 0; y < 16; y++)
      for (let x = 0; x < 16; x++) {
        const c = getPx(img, x, y);
        if ((c & 255) > (getPx(out, x, y) & 255)) setPx(out, x, y, c);
      }
  }
  return out;
}

function beginSpikeSession(doc: LevelDoc, req: TileEditRequest, seed: Img | null): EditSession | string {
  if (!req.corner) return 'No inside corner to draw.';
  const cont = getContainer(doc, req.container);
  const [sx, sy] = solidOfDest(req.x, req.y, dirsOfCorner(req.corner)[0]);
  const info = terrainCellInfo(cont, sx, sy);
  if (!info) return 'No terrain next to this corner.';
  const d = styleData(info.styleId);
  if (!d) return 'Unknown tileset.';
  const ov = styleOverride(info.styleId) ?? {};
  const pe = cornerTile(info.styleId, req.corner);
  // one spike set per tileset: grass colours do not get spike versions of their own
  const base: Omit<EditSession, 'tileN' | 'created' | 'flags' | 'snapshot'> = {
    req,
    styleId: info.styleId,
    d,
    info,
    key: req.corner,
    variants: [],
    cellColor: '',
    cur: -1,
    curPe: pe,
    prev: styleOverride(info.styleId) ? structuredClone(ov) : null,
    baseList: null,
    editLayer: editLayerOf(doc, req, info.styleId),
  };
  if (req.mode === 'edit') {
    const c = pe ? customTileOf(pe) : null;
    if (!c) return 'The tile here is not a custom tile.';
    return { ...base, tileN: c.tile.n, created: false, flags: 0, snapshot: { at: c.tile.updated, px: c.tile.px, variants: c.tile.variants ? { ...c.tile.variants } : undefined } };
  }
  const shown = seed ?? (pe ? spritePixels(pe) ?? compositeSpikeSeed(info.styleId, req.corner) : compositeSpikeSeed(info.styleId, req.corner));
  const t = createCustomTile(info.styleId, shown, { note: `spike ${req.corner} corner` });
  const s: EditSession = { ...base, tileN: t.n, created: true, flags: 0, snapshot: { at: t.made, px: t.px, variants: t.variants } };
  applyLink(s, req.link ?? 'replace');
  return s;
}

/** Start a session; for new tiles and copies the tile is created (unsaved) and linked for the preview. */
export function beginSession(doc: LevelDoc, req: TileEditRequest, seed: Img | null = null): EditSession | string {
  if (req.layer === 'spike') return beginSpikeSession(doc, req, seed);
  const cont = getContainer(doc, req.container);
  const info = terrainCellInfo(cont, req.x, req.y);
  if (!info) return 'No terrain at this cell.';
  const d = styleData(info.styleId);
  if (!d) return 'Unknown tileset.';
  const cs = info[req.layer];
  const general = req.scope === 'general' && cs.general;
  const near = req.scope === 'neighbours' && cs.exact;
  const lit = req.scope === 'light' && cs.tone;
  const key = general ? cs.general!.key : lit ? cs.tone!.key : near ? cs.exact!.key : cs.key;
  if (!key) return req.layer === 'dirt' ? 'This cell is interior fill: it has no dirt case.' : 'Nothing here faces the air: no grass case.';
  const ov = styleOverride(info.styleId) ?? {};
  const table = spriteTable(d, ov);
  const cur = req.layer === 'dirt' ? info.level : info.fg;
  const curPe = cur >= 0 ? variantPe(d, table, cur >> 3, info.variant) : null;
  const variants = d.variants.length > 1 ? d.variants.map((v) => v.name) : [];
  const base: Omit<EditSession, 'tileN' | 'created' | 'flags' | 'snapshot'> = {
    req,
    styleId: info.styleId,
    d,
    info,
    key,
    variants,
    cellColor: info.variant > 0 ? variants[info.variant] ?? '' : '',
    cur,
    curPe,
    prev: styleOverride(info.styleId) ? structuredClone(styleOverride(info.styleId)!) : null,
    baseList: general ? cs.general!.linked : lit ? (cs.tone!.linked ?? cs.exact?.linked ?? cs.caseLinked) : near ? (cs.exact!.linked ?? cs.caseLinked) : cs.caseLinked,
    editLayer: editLayerOf(doc, req, info.styleId),
  };
  if (req.mode === 'edit') {
    const c = cur >= 0 ? customTileOf(table[cur >> 3]) : null;
    if (!c) return 'The tile here is not a custom tile.';
    return { ...base, tileN: c.tile.n, created: false, flags: cur & 7, snapshot: { at: c.tile.updated, px: c.tile.px, variants: c.tile.variants ? { ...c.tile.variants } : undefined } };
  }
  // drawn in the cell's orientation: the copy is stored the way it shows, so it is linked unflipped
  const shown = seed ?? (curPe ? applyFlags(spritePixels(curPe) ?? blank(16), cur & 7) : blank(16));
  const cellColor = base.cellColor;
  const canonical = cellColor ? applySwap(shown, swapMap(info.styleId, cellColor, '')) : shown;
  const t = createCustomTile(info.styleId, canonical, {
    shape: req.layer === 'dirt' ? info.own : undefined,
    note: `${req.layer} case ${key}`,
    variants: cellColor ? { [cellColor]: shown } : undefined,
  });
  const s: EditSession = { ...base, tileN: t.n, created: true, flags: 0, snapshot: { at: t.made, px: t.px, variants: t.variants } };
  applyLink(s, 'replace');
  return s;
}

/** The choice the session's tile has in the style table, adding it to an override copy. */
function tileChoice(s: EditSession, o: StyleOverride): number {
  const pe = customEntry(s.styleId, s.tileN)!;
  const extra = (o.extraSprites ??= []);
  const table = [...s.d.sprites, ...extra];
  let si = table.findIndex((p) => p.tex === pe.tex && p.sprite === pe.sprite);
  if (si < 0) {
    si = table.length;
    extra.push(pe);
  }
  return si * 8;
}

/** Link the new tile to the case for the preview (not saved): alone, or as another variant. */
export function applyLink(s: EditSession, mode: LinkMode) {
  if (!s.created) return;
  const o: StyleOverride = structuredClone(s.prev ?? {});
  if (s.req.layer === 'spike') {
    if (s.req.corner) {
      tileChoice(s, o);
      linkSpikeCorner(o, s.req.corner, customEntry(s.styleId, s.tileN)!);
      setStyleOverride(s.styleId, o);
    }
    return;
  }
  const layer = s.req.layer;
  const c = tileChoice(s, o);
  let ps = pairs(s.baseList ?? s.info[layer].learned);
  if (mode === 'add') {
    // another variant next to the sprites already used ("nothing" / plain fill would dilute it)
    ps = [...ps.filter(([v]) => v !== NONE && v !== FILL), [c, 1]];
  } else if (s.req.mode === 'copy' && ps.some(([v]) => v === s.cur)) {
    ps = ps.map(([v, w]) => [v === s.cur ? c : v, w]);
  } else ps = [[c, 1]];
  const cases = (o.cases ??= {});
  (cases[layer] ??= {})[s.key] = ps.flat();
  setStyleOverride(s.styleId, o);
}

/** Show pixels (canvas orientation) for one colour of the tile, live in the level. */
export function writeColor(s: EditSession, color: string, img: Img) {
  setTilePixels(s.styleId, s.tileN, color || null, applyFlags(img, s.flags, true));
}

export function removeColor(s: EditSession, color: string) {
  if (color) setTilePixels(s.styleId, s.tileN, color, null);
}

export async function saveSession(s: EditSession): Promise<void> {
  const t = findCustomTile(s.styleId, s.tileN);
  if (!t) return;
  if (!s.created) pushHistory(t, s.snapshot);
  else t.updated = new Date().toISOString();
  await Promise.all([saveCustomSheet(s.styleId, 0), s.created ? saveStyleOverride(s.styleId, 0) : Promise.resolve()]);
}

export function cancelSession(s: EditSession) {
  if (s.created) {
    setStyleOverride(s.styleId, s.prev);
    removeCustomTile(s.styleId, s.tileN);
  } else restoreTile(s.styleId, s.tileN, s.snapshot);
}

/** Tile images of every colour the tile has, in canvas orientation ('' = canonical). */
export function sessionImages(s: EditSession): Record<string, Img> {
  const t = findCustomTile(s.styleId, s.tileN);
  const out: Record<string, Img> = {};
  if (!t) return out;
  const pe = customEntry(s.styleId, s.tileN)!;
  out[''] = applyFlags(spritePixels(pe) ?? blank(16), s.flags);
  for (const v of Object.keys(t.variants ?? {})) out[v] = applyFlags(spritePixels({ ...pe, sprite: `${pe.sprite}@${v}` }) ?? blank(16), s.flags);
  return out;
}

// ------------------------------------------------------------------ context around the cell

export interface ContextTile {
  dx: number;
  dy: number;
  pe: PaletteEntry;
  f: number;
  /** on the layer being edited */
  edit: boolean;
}

/** Tiles of the 3×3 cells around the edited cell, in drawing order, with the edited layer's position. */
export function contextTiles(doc: LevelDoc, s: EditSession): { below: ContextTile[]; edit: ContextTile[]; above: ContextTile[] } {
  const cont = getContainer(doc, s.req.container);
  const layers = [...cont.layers].sort((a, b) => sortKey(a.sortingLayer, a.sortingOrder) - sortKey(b.sortingLayer, b.sortingOrder));
  const out = { below: [] as ContextTile[], edit: [] as ContextTile[], above: [] as ContextTile[] };
  let seen = false;
  for (const l of layers) {
    if (l.visible === false) continue;
    const isEdit = l.id === s.editLayer;
    const tiles = layerTiles(doc, cont, l);
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const c = getCell(tiles, s.req.x + dx, s.req.y + dy);
        if (!c || c[0] < 0) continue;
        const pe = doc.palette[c[0]];
        if (!pe) continue;
        const t = { dx, dy, pe, f: c[1], edit: isEdit };
        (isEdit ? out.edit : seen ? out.above : out.below).push(t);
      }
    if (isEdit) seen = true;
  }
  return out;
}

/** Edge pixels whose opacity does not continue into the neighbour on the edited layer. */
export function seamProblems(img: Img, edit: ContextTile[]): { side: 'L' | 'R' | 'B' | 'T'; i: number }[] {
  const out: { side: 'L' | 'R' | 'B' | 'T'; i: number }[] = [];
  const n = img.w;
  const nb = (dx: number, dy: number) => {
    const t = edit.find((e) => e.dx === dx && e.dy === dy);
    const px = t && spritePixels(t.pe);
    return px ? applyFlags(px, t!.f) : null;
  };
  const L = nb(-1, 0), R = nb(1, 0), B = nb(0, -1), T = nb(0, 1);
  for (let i = 0; i < n; i++) {
    const op = (a: Img, x: number, y: number) => getPx(a, x, y) !== 0;
    if (L && op(img, 0, i) !== op(L, n - 1, i)) out.push({ side: 'L', i });
    if (R && op(img, n - 1, i) !== op(R, 0, i)) out.push({ side: 'R', i });
    // rows run from the top: the neighbour below meets the bottom row
    if (B && op(img, i, n - 1) !== op(B, i, 0)) out.push({ side: 'B', i });
    if (T && op(img, i, 0) !== op(T, i, n - 1)) out.push({ side: 'T', i });
  }
  return out;
}

// ------------------------------------------------------------------ palettes

const paletteCache = new Map<string, number[]>();

/** Colours of a tileset's sprites in one colour variant ('' = canonical). */
export function tilesetPalette(styleId: string, color: string): number[] {
  const k = `${styleId}|${color}`;
  const hit = paletteCache.get(k);
  if (hit) return hit;
  const d = styleData(styleId);
  const st = compiledStyle(styleId);
  if (!d || !st) return [];
  const vi = color ? d.variants.findIndex((v) => v.name === color) : 0;
  const vs = vi > 0 ? variantSprites(st, vi) : st.sprites;
  const imgs: Img[] = [];
  for (let si = 0; si < d.sprites.length; si++) {
    const px = spritePixels(vs[si] ?? (vi > 0 ? null : d.sprites[si]));
    if (px) imgs.push(px);
  }
  const p = paletteOf(imgs, 64, 3);
  if (imgs.length) paletteCache.set(k, p);
  return p;
}

const swapCache = new Map<string, Map<number, number>>();

function learnedSwap(styleId: string, from: string, to: string): Map<number, number> {
  const k = `${styleId}|${from}>${to}`;
  const hit = swapCache.get(k);
  if (hit) return hit;
  const d = styleData(styleId);
  const st = compiledStyle(styleId);
  const out = new Map<number, number>();
  if (!d || !st) return out;
  const vi = (c: string) => (c ? d.variants.findIndex((v) => v.name === c) : 0);
  const a = vi(from), b = vi(to);
  if (a < 0 || b < 0) return out;
  const va = a > 0 ? variantSprites(st, a) : st.sprites, vb = b > 0 ? variantSprites(st, b) : st.sprites;
  const pairs: [Img, Img][] = [];
  for (let si = 0; si < d.sprites.length; si++) {
    const pa = va[si], pb = vb[si];
    if (!pa || !pb || (pa.tex === pb.tex && pa.sprite === pb.sprite)) continue;
    const ia = spritePixels(pa), ib = spritePixels(pb);
    if (ia && ib) pairs.push([ia, ib]);
  }
  const m = learnSwap(pairs);
  if (pairs.length) swapCache.set(k, m);
  return m;
}

/** Colour mapping between two colour variants of a tileset, with the hand-edited entries applied. */
export function swapMap(styleId: string, from: string, to: string): Map<number, number> {
  const m = new Map(learnedSwap(styleId, from, to));
  const edits = customSheet(styleId)?.swaps?.[`${from}>${to}`];
  if (edits) for (const [a, b] of Object.entries(edits)) m.set(parseHex(a), parseHex(b));
  return m;
}

// ------------------------------------------------------------------ missing tiles

export interface MissingCase {
  styleId: string;
  layer: 'dirt' | 'grass';
  key: string;
  own: number;
  nb: number[];
  cells: [number, number][];
}

/** Missing-tile cells of a container grouped by tile case (most cells first). */
export function missingCases(doc: LevelDoc, containerId: string, limit = 400): MissingCase[] {
  const cont = getContainer(doc, containerId);
  const groups = new Map<string, MissingCase>();
  for (const m of terrainMissing(doc, cont).slice(0, limit)) {
    const info = terrainCellInfo(cont, m.x, m.y);
    if (!info) continue;
    for (const layer of ['dirt', 'grass'] as const) {
      if (!(layer === 'dirt' ? m.dirt : m.grass)) continue;
      const key = info[layer].key;
      if (!key) continue;
      const k = `${info.styleId}|${layer}|${key}`;
      let g = groups.get(k);
      if (!g) groups.set(k, (g = { styleId: info.styleId, layer, key, own: info.own, nb: info.nb, cells: [] }));
      g.cells.push([m.x, m.y]);
    }
  }
  return [...groups.values()].sort((a, b) => b.cells.length - a.cells.length);
}
